/**
 * SeriesNav — previous/next reading-sequence navigation for the guide and
 * playbook detail pages. Renders between the FAQ section and the CBB CTA:
 * after the reading, before the sales pitch.
 *
 * Pure render component fed a `SeriesNavResult` already computed by
 * `buildSeriesNav` (`~/lib/shared/cih/library-series`) — it never reads a raw
 * `seriesPosition` off a row, only the derived `position`/`total` the caller
 * hands in, so a reader can never be shown a step count that outruns what
 * they can actually click through to.
 *
 * Aliases: series nav, previous next in series, reading sequence nav
 */
import {
  LibraryLink,
  type LibraryLinkEntry,
} from "~/components/cih/library-link";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import type {
  SeriesEntry,
  SeriesGuide,
  SeriesNavResult,
  SeriesPlaybook,
} from "~/lib/shared/cih/library-series";
import { SERIES_FAMILY_LABELS } from "~/lib/shared/content-status";

type Props<TGuide extends SeriesGuide, TPlaybook extends SeriesPlaybook> = {
  readonly nav: SeriesNavResult<TGuide, TPlaybook>;
};

export function SeriesNav<
  TGuide extends SeriesGuide,
  TPlaybook extends SeriesPlaybook,
>({ nav }: Props<TGuide, TPlaybook>) {
  return (
    <CihSection id="series">
      <CihSectionHeader
        label={SERIES_FAMILY_LABELS[nav.family]}
        title={`Step ${nav.position} of ${nav.total}`}
      />
      <nav
        aria-label={`${SERIES_FAMILY_LABELS[nav.family]} navigation`}
        className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 sm:grid-cols-2 xl:px-0"
      >
        <SeriesNeighbor direction="previous" entry={nav.previous} />
        <SeriesNeighbor direction="next" entry={nav.next} />
      </nav>
    </CihSection>
  );
}

function SeriesNeighbor<
  TGuide extends SeriesGuide,
  TPlaybook extends SeriesPlaybook,
>({
  direction,
  entry,
}: {
  readonly direction: "previous" | "next";
  readonly entry: SeriesEntry<TGuide, TPlaybook> | null;
}) {
  if (!entry) {
    // Keeps the two-column grid balanced at the ends of a series rather than
    // collapsing to one wide tile — an end is a fact about the series, not
    // something worth calling more attention to.
    return <div aria-hidden="true" />;
  }

  const isNext = direction === "next";
  const linkEntry: LibraryLinkEntry = entry;

  return (
    <LibraryLink
      entry={linkEntry}
      className={`group flex flex-col gap-2 rounded-2xl border border-gray-100 bg-white p-6 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md ${
        isNext ? "sm:items-end sm:text-right" : ""
      }`}
    >
      <span className="text-[10px] font-medium tracking-[0.22em] text-gray-400 uppercase">
        {isNext ? "Next" : "Previous"}
      </span>
      <span className="font-heading text-lg font-medium tracking-tight text-gray-900">
        {isNext ? (
          <>
            {entry.item.title}{" "}
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
              →
            </span>
          </>
        ) : (
          <>
            <span className="inline-block transition-transform duration-300 group-hover:-translate-x-0.5">
              ←
            </span>{" "}
            {entry.item.title}
          </>
        )}
      </span>
    </LibraryLink>
  );
}
